import { useState, useEffect } from 'react';
import { Link } from 'react-router';
import { Clock, LinkExternal01 } from '@untitledui/icons';
import { ContainerRouteCard } from './container-route-card';
import { accountService } from '@/data/mock-accounts';
import type { Container } from '@/types/container';

interface ContainerInfoPanelProps {
    container: Container;
    lastUpdateTime?: Date;
    onClose?: () => void;
    onContainerSelect?: (containerId: string) => void;
}

export const ContainerInfoPanel = ({
    container,
    lastUpdateTime = new Date(),
    onClose,
    onContainerSelect
}: ContainerInfoPanelProps) => {
    const [timeAgo, setTimeAgo] = useState<string>('');
    const [shipper, setShipper] = useState<any>(null);

    useEffect(() => {
        // Carica info shipper
        const shipperData = accountService.getById(container.shipperId);
        setShipper(shipperData);
    }, [container.shipperId]);

    useEffect(() => {
        const updateTimeAgo = () => {
            const now = new Date();
            const diff = Math.floor((now.getTime() - lastUpdateTime.getTime()) / 1000);
            
            if (diff < 60) {
                setTimeAgo(`${diff} secondi fa`);
            } else if (diff < 3600) {
                const minutes = Math.floor(diff / 60);
                setTimeAgo(`${minutes} ${minutes === 1 ? 'minuto' : 'minuti'} fa`);
            } else {
                const hours = Math.floor(diff / 3600);
                setTimeAgo(`${hours} ${hours === 1 ? 'ora' : 'ore'} fa`);
            }
        };

        updateTimeAgo();
        const interval = setInterval(updateTimeAgo, 60000); // Aggiorna ogni minuto

        return () => clearInterval(interval);
    }, [lastUpdateTime]);

    // Logo carrier - per ora mostriamo solo il nome, in futuro si può aggiungere un logo
    const carrierName = container.shippingCompany || 'Unknown Carrier';

    return (
        <div className="bg-white rounded-lg shadow-xl ring-1 ring-secondary max-w-md w-full">
            {/* Header */}
            <div className="px-6 py-4 border-b border-secondary">
                <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                        {/* Carrier logo placeholder - in futuro si può aggiungere un logo reale */}
                        <div className="size-10 rounded bg-brand-100 dark:bg-brand-900 flex items-center justify-center">
                            <span className="text-xs font-semibold text-brand-600 dark:text-brand-400">
                                {carrierName.substring(0, 2).toUpperCase()}
                            </span>
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold text-primary">{carrierName}</h3>
                            <p className="text-sm text-tertiary">{container.containerNumber}</p>
                        </div>
                    </div>
                    {onClose && (
                        <button
                            onClick={onClose}
                            className="text-tertiary hover:text-primary transition-colors"
                        >
                            <svg className="size-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    )}
                </div>
                
                {/* Location timestamp */}
                <div className="flex items-center gap-2 text-sm text-tertiary">
                    <Clock className="size-4" />
                    <span>Location of Vessel as of {timeAgo}</span>
                </div>
            </div>

            {/* Containers on vessel section */}
            <div className="px-6 py-4">
                <h4 className="text-md font-semibold text-primary mb-3">Containers on vessel</h4>
                <div className="space-y-3">
                    <ContainerRouteCard
                        container={container}
                        onSelect={onContainerSelect}
                        showFlag={true}
                    />
                    
                    {/* Se ci sono altri container sullo stesso vessel, potremmo mostrarli qui */}
                    {/* Per ora mostriamo solo il container selezionato */}
                </div>

                {/* Customer info */}
                {shipper && (
                    <div className="mt-4 pt-4 border-t border-secondary">
                        <div className="text-xs text-tertiary mb-1">Customer</div>
                        <div className="text-sm font-medium text-primary">{shipper.legalName}</div>
                        {shipper.email && (
                            <div className="text-xs text-tertiary">{shipper.email}</div>
                        )}
                    </div>
                )}

                {/* Link to detail */}
                <div className="mt-4">
                    <Link
                        to={`/shipper/containers/${container.id}`}
                        className="inline-flex items-center gap-2 text-sm text-brand-secondary hover:text-brand-secondary_hover"
                    >
                        <LinkExternal01 className="size-4" />
                        View container details
                    </Link>
                </div>
            </div>
        </div>
    );
};

